/**
 * Le document de politiques, vu comme du texte.
 *
 * Fonctions PURES, sans React : elles servent l'éditeur, les diffs entre
 * versions et l'aperçu. Elles vivent ici et non dans la page pour rester
 * lisibles et testables — et pour qu'on puisse s'en servir sans embarquer
 * Chart.js et les modales avec.
 */

/**
 * Le document est une suite de sections `## Titre` — l'unité de lecture ET
 * d'édition. Le préambule (avant la première section) est conservé tel quel et
 * réassemblé, jamais montré.
 */
export const splitSections = (text) => {
    const lines = (text ?? '').split('\n')
    const sections = []
    const preamble = []
    let current = null
    lines.forEach((line) => {
        const m = line.match(/^## (.+)$/)
        if (m) {
            if (current) sections.push(current)
            current = { title: m[1].trim(), body: [] }
        } else if (current) {
            current.body.push(line)
        } else {
            preamble.push(line)
        }
    })
    if (current) sections.push(current)
    return {
        preamble: preamble.join('\n').trim(),
        sections: sections.map((s) => ({ ...s, body: s.body.join('\n').trim() })),
    }
}

export const joinSections = (preamble, sections) =>
    [preamble, ...sections.map((s) => `## ${s.title}\n\n${s.body.trim()}`)].filter(Boolean).join('\n\n') + '\n'

/**
 * Texte « déplié » : le document source est plié à 80 colonnes, l'éditeur
 * WYSIWYG ressort des paragraphes entiers — même texte pour le modèle.
 */
export const unwrap = (t) =>
    (t ?? '')
        .trim()
        .split(/\n\s*\n/)
        .map((block) => {
            const out = []
            for (const line of block.split('\n')) {
                if (/^\s*([-*]|\d+\.)\s+/.test(line) || !out.length) out.push(line.replace(/\s+$/, ''))
                else out[out.length - 1] += ' ' + line.trim()
            }
            return out.join('\n')
        })
        .join('\n\n')

/** Lignes logiques, pour comparer deux textes ligne à ligne. */
const logicalLines = (s) => unwrap(s).replace(/\n\n+/g, '\n')

/**
 * Deux textes disent-ils la même chose ? Comparaison aux MOTS, la même règle
 * que `PromptVersion::sameText()` côté serveur — sinon l'écran annonce une
 * modification que le serveur refuse.
 */
export const sameText = (a, b) => (a ?? '').trim().replace(/\s+/g, ' ') === (b ?? '').trim().replace(/\s+/g, ' ')

/** Sections touchées entre deux documents : ajoutée, supprimée, modifiée. */
export const diffSections = (before, after) => {
    const a = splitSections(before).sections
    const b = splitSections(after).sections
    const A = Object.fromEntries(a.map((s) => [s.title, s]))
    const B = Object.fromEntries(b.map((s) => [s.title, s]))
    const out = []
    b.forEach((s) => {
        if (!A[s.title]) out.push({ title: s.title, kind: 'added', after: s.body })
        else if (!sameText(A[s.title].body, s.body))
            out.push({ title: s.title, kind: 'changed', before: A[s.title].body, after: s.body })
    })
    a.forEach((s) => {
        if (!B[s.title]) out.push({ title: s.title, kind: 'removed', before: s.body })
    })
    return out
}

/** Diff de lignes logiques (plus longue sous-séquence commune). */
export const diffLines = (before, after) => {
    const a = logicalLines(before).split('\n')
    const b = logicalLines(after).split('\n')
    const n = a.length
    const m = b.length
    const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
    for (let i = n - 1; i >= 0; i--)
        for (let j = m - 1; j >= 0; j--)
            dp[i][j] = a[i].trim() === b[j].trim() ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
    const out = []
    let i = 0
    let j = 0
    while (i < n && j < m) {
        if (a[i].trim() === b[j].trim()) {
            out.push({ kind: 'same', text: a[i] })
            i++
            j++
        } else if (dp[i + 1][j] >= dp[i][j + 1]) out.push({ kind: 'removed', text: a[i++] })
        else out.push({ kind: 'added', text: b[j++] })
    }
    while (i < n) out.push({ kind: 'removed', text: a[i++] })
    while (j < m) out.push({ kind: 'added', text: b[j++] })
    return out
}

/** Une ligne markdown rendue lisible en texte nu, pour les diffs. */
export const plainLine = (line) =>
    line
        .replace(/^\s*[-*]\s+/, '• ')
        .replace(/^\s{2,}/, '   ')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/`(.+?)`/g, '$1')
